import { useRef, useState } from 'react';
import { Portal } from 'react-portal';
import { usePreventBodyScroll } from './hooks';
import { useClickOutside, useEffectOnce } from '../../hooks';
import { CloseButton } from '../button';
import { useScroll } from './hooks/use-scroll';
import { mergeClassNames } from '../../style';

export type ModalV2Props = {
  closeOnOutsideClick?: boolean;
  children: JSX.Element;
  footer?: JSX.Element;
  header?: JSX.Element;
  onClose: VoidFunction;
  style?: {
    height?: string;
    width?: string;
    paddingBottom?: string;
    paddingTop?: string;
    paddingRight?: string;
    paddingLeft?: string;
  };
  withCloseButton?: boolean;
};

export const ModalV2 = ({
  closeOnOutsideClick = false,
  children,
  footer,
  header,
  onClose,
  style,
  withCloseButton = false,
}: ModalV2Props): JSX.Element => {
  const modalRef = useRef<HTMLDivElement>(null);
  const modalContainerRef = useRef<HTMLDivElement>(null);
  const [modalContainerElement, setModalContainerElement] = useState<HTMLElement | null>(null);
  const { isScrollOnTop, isScrollOnBottom, onScroll, hasScrollBar } = useScroll(modalContainerElement);

  useEffectOnce(() => {
    setModalContainerElement(modalContainerRef.current);
  });

  const showHeaderRow = header !== undefined || withCloseButton;
  const showFooterRow = footer !== undefined;

  useClickOutside({ ref: modalRef, onClickOutside: onClose, skip: !closeOnOutsideClick, withEscKeyListener: true });
  usePreventBodyScroll();

  const modalContainerClassName = mergeClassNames([
    { className: 'core-modal-v2__content-container' },
    {
      className: 'core-modal-v2__content-container-top-bottom-shadow',
      include: hasScrollBar && !isScrollOnTop && !isScrollOnBottom,
    },
    {
      className: 'core-modal-v2__content-container-top-shadow',
      include: hasScrollBar && !isScrollOnTop && isScrollOnBottom,
    },
    {
      className: 'core-modal-v2__content-container-bottom-shadow',
      include: hasScrollBar && isScrollOnTop && !isScrollOnBottom,
    },
  ]);

  return (
    <Portal node={document && document.getElementById('modal-v2-root')}>
      <div className="core-modal-v2__background-overlay" />
      <div ref={modalRef} className="core-modal-v2__container" style={style}>
        {showHeaderRow && (
          <div className="core-modal-v2__header">
            <div className="core-modal-v2__header-container">
              {header}
              {withCloseButton && <CloseButton onClose={onClose} ariaLabel="Close modal" />}
            </div>
          </div>
        )}
        <div ref={modalContainerRef} className={modalContainerClassName} onScroll={onScroll}>
          <div className="core-modal-v2__content">{children}</div>
        </div>

        {showFooterRow && <div className="core-modal-v2__footer">{footer}</div>}
      </div>
    </Portal>
  );
};
